import { FormComponent } from "../../../react-core/Html/FormComponent";
import * as React from "react";
import { PartLoading } from "../../../react-core/Html/PartLoading";
import { getQueryParameters } from "../Wizards/Entities/QueryParametersHelper";
import { IProcessProps, IStepConfig, Process } from "./Process/Process";
import { uniq } from "lodash";
import { Lightbox } from "../../../react-core/Html/Components/Lightbox";

export interface IWizardComponentStateGeneric {
    data: any;
    errorMsg: string[];
    isLoading: boolean;
    isShowErrorMsg: boolean;
    onPosting?: boolean;
    active?: boolean;
    isSubmitting?: boolean;
    showSaveAndExitLightbox?: boolean;
    hasPassBeforeExit?: boolean;
    saveFrom?: number;
}

export abstract class WizardComponentGeneric<TState extends IWizardComponentStateGeneric> extends FormComponent<
    null,
    TState
> {
    constructor(props: any) {
        super(props);
        this.initState();
    }

    protected stepperConfig: IStepConfig[] = [];
    protected child: any = {};


    /**
     * @param {any}  oldVal - An old value to compare against
     * @param {any} newVal - A new value to compare against
     * @returns {any} Returns the difference between the old and new values. (!!!)in case diff is absent, returns empty object
     */
    private deepDiff(oldVal, newVal) {
        const diff = {};

        if (oldVal === newVal) return diff; 
        if (newVal === undefined) return null;

        if (oldVal === null || newVal === null || oldVal === undefined ||
            typeof oldVal !== 'object' || typeof newVal !== 'object' ||
            Array.isArray(newVal) || Array.isArray(oldVal)) {
            return newVal;
        }

        const keys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);
        for (const key of Array.from(keys)) {
            const subDiff = this.deepDiff(oldVal[key], newVal[key]);
            if (JSON.stringify(subDiff) !== '{}') diff[key] = subDiff;
        }

        return diff;
    }

    /**
     * @param {any}  target - An old value of the state to updated
     * @param {any} source - A new values to substittue old values with
     * @returns {any} Returns the updated state
     */
    private deepChange(target, source) {
        for (const key in source) {
            if (source[key] && typeof source[key] === "object" && !Array.isArray(source[key])) {
                if (!target[key] || typeof target[key] !== "object" || Array.isArray(target[key])) {target[key] = {};}
                this.deepChange(target[key], source[key]);
            } else {
                target[key] = source[key];
            }
        }
        return target;
    }

    // TODO: consider using immer, or loadash for deep cloning
    // manual deep cloning function is not good, it may not cover all edge cases,
    // some people already did better than us
    /**
     * @param {any}  changes - An object conains of the Key value pairs, that need to be changed
     * @description This function is used to update the state of the react component with the changes provided.
     */
    protected onChange(changes) {
        const {data} = this.state
        const _changes = JSON.parse(JSON.stringify(changes));

        this.setState((preState) => {
            const diff = {}
            for (const key in _changes) {
                const deepDiff =  this.deepDiff(data[key], _changes[key]);

                if (JSON.stringify(deepDiff) !== '{}'){
                    diff[key] = this.deepDiff(data[key], _changes[key]);
                }
            }
            return {data: this.deepChange(JSON.parse(JSON.stringify(preState.data)), diff)};
        });
    }

    protected getNextStepUrls(to) {
        if (this.stepperConfig[to]) {return this.stepperConfig[to].url;}
        return this.stepperConfig[0].url;
    }

    protected stepValid(step) {
        return this.child[step].isValid();
    }

    protected onStepperLeave(from, to): void {
        if (!this.state.active && to == -1) {return this.onExit();}
        const stepLength = this.stepperConfig.length;
        if (from == stepLength - 1 && (to > from || to == -1)) {return this.onExit();}
        let isPass = false;

        //save & exit
        if (to == -1 && this.state.active) {
            isPass = this.child[from]?.isValid();
            this.setState({ showSaveAndExitLightbox: true, hasPassBeforeExit: isPass, saveFrom: from });
            return;
        }
        if (from > to || !this.state.active) {
            isPass = true;
        } else {
            isPass = this.child[from]?.isValid();
        }
        const params = getQueryParameters();
        const url = this.getNextStepUrls(to);
        if (isPass) {
            this.setState({ onPosting: true });
            this.saveInstruction(from, to, url, params, stepLength);
        } else {
            this.setState((preState) => ({ isShowErrorMsg: preState.errorMsg.length > 0 }));
        }
    }
    abstract initState();

    abstract saveInstruction(from, to, toUrl, params, stepLength);

    abstract onCancel();

    abstract onExit();

    abstract onSubmit();

    protected getStepProps(index) {
        const { data, active } = this.state;
        return {
            isReview: !active,
            data: JSON.parse(JSON.stringify(data)),
            onRef: (ref) => (this.child[index] = ref),
            onChange: (changes) => this.onChange(changes),
            showErrorMsgPopUp: (msg, showNow?) =>
                this.setState((preState) => ({
                    errorMsg: uniq(preState.errorMsg.concat(msg)),
                    isShowErrorMsg: showNow,
                })),
        };
    }

    abstract setStepperConfig();

    protected setProcessProps(): IProcessProps {
        return {
            onPosting: this.state.onPosting,
            stepsConfig: this.stepperConfig,
            onSave: (from) => this.onStepperLeave(from, -1),
            onCancel: () => this.onCancel(),
            processTitle: "",
            goBacktoFirst: false,
            active: this.state.active,
            isShowError: this.state.isShowErrorMsg,
            errorMsg: this.state.errorMsg,
            needSplitHeader: {
                fromStep: 0,
                splitHeaderMain: "",
                splitHeaderSub: "",
            },
            onClearError: () => this.setState({ isShowErrorMsg: false, errorMsg: [] }),
        };
    }

    protected wizardRender() {
        if (this.state.isLoading) {return <PartLoading isLoading={true} />;}
        this.setStepperConfig();
        return (
            <>
                <Process {...this.setProcessProps()} />
            </>
        );
    }

    protected customeRender() { }

    render() {
        return (
            <>
                {this.wizardRender()}
                {this.customeRender()}
                {this.state.showSaveAndExitLightbox && (
                    <Lightbox
                        title="Save & exit"
                        show={this.state.showSaveAndExitLightbox}
                        cssWrapper="is-default-lightbox"
                        onClose={() => this.setState({ showSaveAndExitLightbox: false })}
                    >
                        <div className="mb-40">
                            {this.state.hasPassBeforeExit
                                ? "Are you sure you want to exit from this journey?"
                                : "In order to save all the data from the current page of the journey, you need to complete all mandatory fields.  All completed pages have been automatically saved.  If you choose to exit now you will lose all data entered on current page. "}
                        </div>

                        {this.state.hasPassBeforeExit ? (
                            <>
                                <button
                                    className="btn secondary mr-10"
                                    onClick={() => this.setState({ showSaveAndExitLightbox: false })}
                                >
                                    No
                                </button>
                                <button
                                    className={`btn primary mr-10 ${this.state.onPosting ? "is-busy" : ""}`}
                                    onClick={() => {
                                        const params = getQueryParameters();
                                        const url = this.getNextStepUrls(-1);
                                        const stepLength = this.stepperConfig.length;
                                        this.setState({ onPosting: true });
                                        this.saveInstruction(this.state.saveFrom, -1, url, params, stepLength);
                                    }}
                                >
                                    Yes
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="btn secondary mr-5"
                                    onClick={() => this.setState({ showSaveAndExitLightbox: false })}
                                >
                                    Return
                                </button>
                                <button className="btn primary mr-5" onClick={() => this.onExit()}>
                                    Exit
                                </button>
                            </>
                        )}
                    </Lightbox>
                )}
            </>
        );
    }
}
